import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "../components/ui/dialog";
import { DatePicker } from "../components/DatePicker";
import {
    Inbox,
    Upload,
    Loader2,
    UserCheck,
    CheckCircle2,
    AlertTriangle,
    Trash2,
    FilePlus2,
    User,
    Users,
    Download,
    FileWarning,
    RotateCcw,
    Sparkles,
    Eye,
    ShieldCheck,
    UsersRound,
    Search,
} from "lucide-react";
import { toast } from "sonner";
import { formatBytes, formatDateTime, CATEGORY_LABELS, CATEGORY_ORDER } from "../lib/documents";
import { DocumentPreviewDialog } from "../components/DocumentPreviewDialog";
import { AuditAssignmentsDialog } from "../components/AuditAssignmentsDialog";
import { ZipImportCard } from "../components/ZipImportCard";
import { Checkbox } from "../components/ui/checkbox";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "../components/ui/alert-dialog";

export default function PendingDocumentsPage() {
    const [items, setItems] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [groups, setGroups] = useState([]);
    const [previewDoc, setPreviewDoc] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);
    const [uploadBusy, setUploadBusy] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(null); // {done, total, batches, batchIdx, errors}
    const [defaultCategory, setDefaultCategory] = useState("");
    const [notifyEmails, setNotifyEmails] = useState(true);
    const [defaultExpires, setDefaultExpires] = useState("");
    const [defaultSignedOnPaper, setDefaultSignedOnPaper] = useState(false);
    const [errorReport, setErrorReport] = useState(null); // { errors: [{filename, error}], at }
    const [showErrorsDialog, setShowErrorsDialog] = useState(false);
    const [viewMode, setViewMode] = useState("list"); // "list" | "grouped"
    const [groupAssignBusy, setGroupAssignBusy] = useState(false);
    const [groupDepth, setGroupDepth] = useState(2); // 1 = cognome, 2 = cognome+nome, 3 = +categoria
    const [reassignBusy, setReassignBusy] = useState(false);
    const [auditOpen, setAuditOpen] = useState(false);
    const fileInputRef = useRef(null);
    // Cache of File objects from the last selection — keyed by filename — used by "Riprova solo i falliti"
    const lastFilesRef = useRef(new Map());

    const fetchAll = async () => {
        setLoading(true);
        try {
            const [p, e, g] = await Promise.all([
                api.get("/documents/pending"),
                api.get("/employees"),
                api.get("/groups").catch(() => ({ data: [] })),
            ]);
            setItems(p.data);
            setEmployees(e.data);
            setGroups(g.data || []);
        } catch (err) {
            toast.error("Errore nel caricamento");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAll();
    }, []);

    const empById = useMemo(() => {
        const m = new Map();
        employees.forEach((e) => m.set(e.id, e));
        return m;
    }, [employees]);

    const empOptions = useMemo(() => {
        return employees.map((e) => ({
            id: e.id,
            label: `${e.cognome} ${e.nome} · matr. ${e.matricola}`,
            cessato: !!(e.data_cessazione && String(e.data_cessazione).trim()),
        }));
    }, [employees]);

    const BATCH_SIZE = 50;

    const handleBulkUpload = async (filesList) => {
        if (!filesList || filesList.length === 0) return;
        const allFiles = Array.from(filesList);
        // Cache files by name so we can retry only failed ones later.
        // If duplicates exist, the last one wins (acceptable for retry use case).
        for (const f of allFiles) {
            lastFilesRef.current.set(f.name, f);
        }
        const totalBatches = Math.ceil(allFiles.length / BATCH_SIZE);
        setUploadBusy(true);
        setUploadProgress({
            done: 0,
            total: allFiles.length,
            batches: totalBatches,
            batchIdx: 0,
            errors: 0,
        });

        let totalCreated = 0;
        let totalPending = 0;
        let totalSkipped = 0;
        let totalDuplicates = 0;
        let totalErrors = 0;
        const allErrors = []; // collect ALL errors, not just samples
        const MAX_TRACKED_ERRORS = 5000; // safety cap to avoid huge memory

        for (let i = 0; i < allFiles.length; i += BATCH_SIZE) {
            const batch = allFiles.slice(i, i + BATCH_SIZE);
            const batchIdx = Math.floor(i / BATCH_SIZE) + 1;
            const fd = new FormData();
            for (const f of batch) fd.append("files", f);
            if (defaultCategory) fd.append("category", defaultCategory);
            fd.append("notify", notifyEmails ? "true" : "false");
            if (defaultExpires) fd.append("expires_at", defaultExpires);
            if (defaultCategory === "contratto" && defaultSignedOnPaper) fd.append("signed_on_paper", "true");
            try {
                const { data } = await api.post("/documents/bulk", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                    timeout: 600000,
                });
                totalCreated += data.created || 0;
                totalPending += data.pending || 0;
                totalSkipped += data.skipped || 0;
                totalDuplicates += data.duplicates || 0;
                if (data.errors?.length) {
                    totalErrors += data.errors.length;
                    if (allErrors.length < MAX_TRACKED_ERRORS) {
                        allErrors.push(...data.errors.slice(0, MAX_TRACKED_ERRORS - allErrors.length));
                    }
                }
            } catch (e) {
                totalErrors += batch.length;
                const detail = e.response?.data?.detail || e.message || "errore";
                // Batch-level failure: record each file in the batch
                for (const f of batch) {
                    if (allErrors.length < MAX_TRACKED_ERRORS) {
                        allErrors.push({ filename: f.name, error: `Lotto ${batchIdx}: ${detail}` });
                    }
                }
            }
            setUploadProgress({
                done: Math.min(i + BATCH_SIZE, allFiles.length),
                total: allFiles.length,
                batches: totalBatches,
                batchIdx,
                errors: totalErrors,
                success: totalCreated + totalPending,
            });
        }

        // Final summary toast
        const parts = [];
        if (totalCreated) parts.push(`${totalCreated} assegnati`);
        if (totalPending) parts.push(`${totalPending} in attesa`);
        if (totalSkipped) parts.push(`${totalSkipped} saltati`);
        if (totalDuplicates) parts.push(`${totalDuplicates} duplicati ignorati`);
        if (totalErrors) parts.push(`${totalErrors} errori`);
        const message = parts.join(" · ") || "Nessun file processato";
        if (totalErrors > 0) {
            toast.warning(message, {
                description: "Clicca \"Vedi dettagli errori\" per la lista completa",
                duration: 5000,
            });
            setErrorReport({ errors: allErrors, at: new Date().toISOString() });
        } else {
            toast.success(message);
            setErrorReport(null);
        }

        await fetchAll();
        if (fileInputRef.current) fileInputRef.current.value = "";
        setUploadBusy(false);
        setUploadProgress(null);
    };

    const retryFailedFiles = async () => {
        if (!errorReport?.errors?.length) return;
        // Filter out errors that are "lotto-level" (failed batch) — we still have the File objects for those
        // because they were in the original selection.
        const failedNames = errorReport.errors.map((e) => e.filename).filter(Boolean);
        const filesToRetry = [];
        const missing = [];
        for (const name of failedNames) {
            const f = lastFilesRef.current.get(name);
            if (f) filesToRetry.push(f);
            else missing.push(name);
        }
        if (filesToRetry.length === 0) {
            toast.error("Impossibile riprovare: i file originali non sono più disponibili. Riseleziona dal disco.");
            return;
        }
        if (missing.length > 0) {
            toast.warning(`${missing.length} file non più disponibili — riprovo solo ${filesToRetry.length}`);
        }
        setShowErrorsDialog(false);
        // Clear the previous error report before retrying so the banner reflects only the new attempt
        setErrorReport(null);
        await handleBulkUpload(filesToRetry);
    };

    const confirmAssign = async (doc, employeeId, category, expiresAt) => {
        setBusyId(doc.id);
        try {
            await api.post(`/documents/pending/${doc.id}/assign`, {
                employee_id: employeeId,
                category: category || doc.category,
                expires_at: expiresAt || null,
            });
            toast.success("Documento assegnato");
            setItems((prev) => prev.filter((d) => d.id !== doc.id));
        } catch (e) {
            const detail = e.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Errore assegnazione");
        } finally {
            setBusyId(null);
        }
    };

    const deletePending = async (doc) => {
        if (!window.confirm(`Eliminare il documento "${doc.filename}"?`)) return;
        setBusyId(doc.id);
        try {
            await api.delete(`/documents/${doc.id}`);
            toast.success("Documento eliminato");
            setItems((prev) => prev.filter((d) => d.id !== doc.id));
        } catch (e) {
            toast.error("Errore eliminazione");
        } finally {
            setBusyId(null);
        }
    };

    // Group pending documents by the FIRST N tokens of the filename (N = groupDepth).
    // depth 1 = just cognome → all "carucci-*" together
    // depth 2 = cognome+nome → "carucci-antonio-*" together (DEFAULT, best balance)
    // depth 3 = cognome+nome+category → narrower groups
    const groupedItems = useMemo(() => {
        if (items.length === 0) return [];
        const tokenize = (name) => name.replace(/\.[^.]+$/, "").split(/[-_. ]+/).filter(Boolean);

        const buckets = new Map(); // key -> { key, prefix, docs }
        for (const doc of items) {
            const tokens = tokenize(doc.filename);
            const slice = tokens.slice(0, groupDepth);
            const key = slice.join("-").toLowerCase();
            if (!key) continue;
            if (!buckets.has(key)) {
                buckets.set(key, { key, prefix: slice.join("-"), docs: [] });
            }
            buckets.get(key).docs.push(doc);
        }

        const groups = Array.from(buckets.values());
        // Sort by size desc, then alphabetically
        groups.sort((a, b) => {
            if (b.docs.length !== a.docs.length) return b.docs.length - a.docs.length;
            return a.prefix.localeCompare(b.prefix);
        });
        return groups;
    }, [items, groupDepth]);

    const bulkAssignGroup = async (docs, employeeId, category, expiresAt) => {
        if (!employeeId) {
            toast.error("Seleziona un dipendente");
            return;
        }
        setGroupAssignBusy(true);
        try {
            const { data } = await api.post("/documents/pending/bulk-assign", {
                document_ids: docs.map((d) => d.id),
                employee_id: employeeId,
                category: category || null,
                expires_at: expiresAt || null,
            });
            const { assigned = 0, skipped = 0, not_found = 0 } = data;
            // Remove successfully assigned docs from local state immediately for snappy UX
            const assignedIds = new Set(docs.slice(0, assigned).map((d) => d.id));
            setItems((prev) => prev.filter((d) => !assignedIds.has(d.id)));
            if (assigned > 0 && skipped === 0 && not_found === 0) {
                toast.success(`${assigned} documenti assegnati`);
            } else {
                toast.warning(`${assigned} assegnati, ${skipped} saltati, ${not_found} non trovati`);
            }
            // Fetch in background to reconcile any drift (no await on UI)
            fetchAll();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore durante l'assegnazione massiva");
        } finally {
            setGroupAssignBusy(false);
        }
    };

    // Bulk delete: soft-delete all docs in a group that the user does not want to keep
    const bulkDeleteGroup = async (docs) => {
        if (!docs?.length) return;
        setGroupAssignBusy(true);
        try {
            const ids = docs.map((d) => d.id);
            const { data } = await api.post("/documents/pending/bulk-delete", {
                document_ids: ids,
            });
            const { deleted = 0, not_found = 0 } = data;
            const deletedSet = new Set(ids);
            setItems((prev) => prev.filter((d) => !deletedSet.has(d.id)));
            if (deleted > 0 && not_found === 0) {
                toast.success(`${deleted} file eliminati`);
            } else {
                toast.warning(`${deleted} eliminati, ${not_found} non trovati`);
            }
            fetchAll();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore durante l'eliminazione massiva");
        } finally {
            setGroupAssignBusy(false);
        }
    };

    // Multi-employee assign: distribute a single pending doc to N selected employees at once
    const assignDocToMulti = async (doc, employeeIds, category, expiresAt) => {
        if (!employeeIds?.length) {
            toast.error("Seleziona almeno un dipendente");
            return;
        }
        try {
            const { data } = await api.post(`/documents/pending/${doc.id}/assign-multi`, {
                employee_ids: employeeIds,
                category: category || null,
                expires_at: expiresAt || null,
            });
            const { assigned_to = 0, not_found = 0 } = data;
            toast.success(
                `Documento assegnato a ${assigned_to} dipendenti${not_found > 0 ? ` (${not_found} non trovati)` : ""}`
            );
            // Remove the original pending doc — copies are NOT pending so the page reflects reality
            setItems((prev) => prev.filter((d) => d.id !== doc.id));
            fetchAll();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore distribuzione multipla");
        }
    };

    const reassignAuto = async () => {
        setReassignBusy(true);
        try {
            const { data } = await api.post("/documents/pending/reassign-auto");
            const { checked, assigned, suggested } = data;
            if (assigned > 0) {
                toast.success(`${assigned} file assegnati automaticamente${suggested > 0 ? ` (+${suggested} suggeriti)` : ""} (su ${checked} verificati)`);
            } else if (suggested > 0) {
                toast.info(`Nessun match certo, ma ${suggested} file hanno ora un suggerimento.`);
            } else {
                toast.warning(`${checked} file analizzati, nessun match automatico trovato.`);
            }
            await fetchAll();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore durante la riassegnazione");
        } finally {
            setReassignBusy(false);
        }
    };

    const assignDocToGroup = async (doc, groupId, category) => {
        if (!groupId) {
            toast.error("Seleziona un gruppo");
            return;
        }
        try {
            const { data } = await api.post(`/documents/pending/${doc.id}/assign-to-group`, {
                group_id: groupId,
                category: category || null,
            });
            toast.success(`Documento distribuito al gruppo "${data.group}" (${data.distributed_to} dipendenti)`);
            // Remove the now-assigned doc from local state (the copies are not pending)
            setItems((prev) => prev.filter((d) => d.id !== doc.id));
            fetchAll();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore distribuzione al gruppo");
        }
    };

    return (
        <div data-testid="pending-page">
            <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
                <div className="mb-6 flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                        <div className="flex h-12 w-12 items-center justify-center rounded-md bg-[#376DB3]/10 text-[#376DB3]">
                            <Inbox className="h-6 w-6" />
                        </div>
                        <div>
                            <h1 className="font-display text-2xl font-bold text-[#376DB3]">Documenti in attesa di assegnazione</h1>
                            <p className="text-sm text-slate-600">
                                Carica più documenti insieme: quelli con nome riconosciuto verranno assegnati automaticamente,
                                gli altri restano qui finché non li smisti manualmente.
                            </p>
                        </div>
                    </div>
                    <Button
                        variant="outline"
                        onClick={() => setAuditOpen(true)}
                        className="flex-shrink-0 gap-2 border-violet-300 bg-violet-50 text-violet-700 hover:bg-violet-100"
                        data-testid="open-audit-btn-header"
                        title="Verifica i documenti già assegnati per individuare errori"
                    >
                        <ShieldCheck className="h-4 w-4" />
                        Verifica assegnazioni
                    </Button>
                </div>

                {/* ZIP import (OneDrive folders) */}
                <ZipImportCard employees={employees} onImported={fetchAll} />

                {/* Bulk upload card */}
                <div className="mb-8 rounded-lg border border-slate-200 bg-white p-6 shadow-sm" data-testid="bulk-upload-card">
                    <div className="mb-4 flex items-center gap-2">
                        <FilePlus2 className="h-5 w-5 text-[#376DB3]" />
                        <h2 className="font-display text-lg font-semibold text-slate-800">Upload massivo</h2>
                    </div>
                    <div className="grid gap-4 sm:grid-cols-3">
                        <div>
                            <Label>Categoria (opzionale)</Label>
                            <Select value={defaultCategory || "auto"} onValueChange={(v) => setDefaultCategory(v === "auto" ? "" : v)}>
                                <SelectTrigger className="mt-1.5" data-testid="bulk-category">
                                    <SelectValue placeholder="Auto (da nome file)" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="auto">Auto (da nome file)</SelectItem>
                                    {CATEGORY_ORDER.map((c) => (
                                        <SelectItem key={c} value={c}>{CATEGORY_LABELS[c]}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {defaultCategory === "contratto" && (
                                <label className="mt-2 flex cursor-pointer items-start gap-2 text-xs text-slate-600" data-testid="bulk-signed-on-paper-wrap">
                                    <input
                                        type="checkbox"
                                        checked={defaultSignedOnPaper}
                                        onChange={(e) => setDefaultSignedOnPaper(e.target.checked)}
                                        className="mt-0.5"
                                        data-testid="bulk-signed-on-paper"
                                    />
                                    <span>
                                        <span className="font-medium text-indigo-700">Firmati in originale cartaceo</span>
                                        <span className="block text-slate-500">File redatti non scansionati: l'originale firmato è su carta</span>
                                    </span>
                                </label>
                            )}
                        </div>
                        <div>
                            <Label>Scadenza (opzionale)</Label>
                            <div className="mt-1.5">
                                <DatePicker
                                    value={defaultExpires}
                                    onChange={setDefaultExpires}
                                    placeholder="Nessuna scadenza"
                                    fromYear={new Date().getFullYear()}
                                    toYear={new Date().getFullYear() + 5}
                                    data-testid="bulk-expires"
                                />
                            </div>
                            <p className="mt-1 text-xs text-slate-500">Vuoto = il documento non scade</p>
                            <label className="mt-2 flex cursor-pointer items-start gap-2 text-xs text-slate-600" data-testid="massive-notify-wrap">
                                <input
                                    type="checkbox"
                                    checked={notifyEmails}
                                    onChange={(e) => setNotifyEmails(e.target.checked)}
                                    className="mt-0.5"
                                    data-testid="massive-notify-checkbox"
                                />
                                <span>
                                    <span className="font-medium text-slate-800">Invia email di notifica ai dipendenti</span>
                                    <span className="block text-slate-500">Email "nuovo documento disponibile" per ogni file assegnato automaticamente</span>
                                </span>
                            </label>
                        </div>
                        <div className="flex items-end">
                            <input
                                ref={fileInputRef}
                                type="file"
                                multiple
                                accept="application/pdf,image/*"
                                onChange={(e) => handleBulkUpload(e.target.files)}
                                className="hidden"
                                data-testid="bulk-files"
                            />
                            <Button
                                onClick={() => fileInputRef.current?.click()}
                                disabled={uploadBusy}
                                className="w-full gap-2 bg-[#376DB3] text-white hover:bg-[#376DB3]/90"
                                data-testid="bulk-upload-btn"
                            >
                                {uploadBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                                Seleziona file
                            </Button>
                        </div>
                    </div>
                    <div className={`mt-3 rounded-md border border-blue-200 bg-blue-50 p-3 ${uploadProgress ? "" : "hidden"}`} data-testid="upload-progress">
                        <div className="flex items-center justify-between text-sm font-medium text-blue-900">
                            <span>
                                Lotto {uploadProgress?.batchIdx || 0} di {uploadProgress?.batches || 0}
                                <span className={`ml-2 rounded bg-emerald-100 px-1.5 py-0.5 text-[11px] font-semibold text-emerald-700 ${uploadProgress?.success > 0 ? "" : "hidden"}`}>
                                    ✓ {uploadProgress?.success || 0} riusciti
                                </span>
                                <span className={`ml-2 rounded bg-red-100 px-1.5 py-0.5 text-[11px] font-semibold text-red-700 ${uploadProgress?.errors > 0 ? "" : "hidden"}`}>
                                    ✗ {uploadProgress?.errors || 0} falliti
                                </span>
                            </span>
                            <span className="font-mono-numbers">
                                {uploadProgress?.done || 0} / {uploadProgress?.total || 0}
                            </span>
                        </div>
                        <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-blue-100">
                            <div
                                className="h-full bg-[#376DB3] transition-all"
                                style={{ width: `${uploadProgress?.total ? Math.round((uploadProgress.done / uploadProgress.total) * 100) : 0}%` }}
                            />
                        </div>
                        <div className="mt-1 text-xs text-blue-700">
                            Carico {BATCH_SIZE} file alla volta per evitare timeout. Lascia la finestra aperta.
                        </div>
                    </div>
                    {/* Persistent error report banner — visible after upload completes with errors */}
                    <div className={`mt-3 flex items-start gap-3 rounded-md border border-amber-300 bg-amber-50 p-3 ${errorReport && !uploadBusy ? "" : "hidden"}`} data-testid="error-report-banner">
                        <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-700" />
                        <div className="flex-1 text-sm text-amber-900">
                            <div className="font-medium">
                                {errorReport?.errors?.length || 0} file non sono stati caricati nell'ultimo upload.
                            </div>
                            <div className="mt-0.5 text-xs">
                                Le cause più comuni sono: file vuoto, file &gt; 50 MB o problemi di rete.
                            </div>
                        </div>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={retryFailedFiles}
                            disabled={uploadBusy}
                            className="border-emerald-300 bg-white text-emerald-700 hover:bg-emerald-50"
                            data-testid="retry-failed-btn"
                        >
                            <RotateCcw className="mr-1 h-3.5 w-3.5" />
                            Riprova {errorReport?.errors?.length || 0} file
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setShowErrorsDialog(true)}
                            className="border-amber-300 bg-white"
                            data-testid="view-errors-btn"
                        >
                            Vedi dettagli
                        </Button>
                        <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setErrorReport(null)}
                            className="text-amber-700"
                            data-testid="dismiss-errors-btn"
                        >
                            Chiudi
                        </Button>
                    </div>
                    <div className={`mt-3 text-xs text-slate-500 ${uploadProgress ? "hidden" : ""}`}>
                        💡 Il sistema riconosce automaticamente cognome+nome nel nome del file (es. <code>abaterusso-catia-cedolino-01-2024.pdf</code>) e li assegna direttamente. Puoi caricare anche centinaia di file insieme: verranno divisi automaticamente in lotti da {BATCH_SIZE}.
                    </div>
                </div>

                {/* Pending list: always-mounted blocks toggled via `hidden` to avoid React 19 insertBefore bug */}
                <div className={`flex items-center justify-center py-16 text-slate-500 ${loading ? "" : "hidden"}`}>
                    <Loader2 className="h-6 w-6 animate-spin" />
                </div>
                <div
                    className={`rounded-lg border border-dashed border-slate-300 bg-white py-16 text-center ${!loading && items.length === 0 ? "" : "hidden"}`}
                    data-testid="pending-empty"
                >
                    <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-400" />
                    <div className="mt-3 font-display text-lg font-semibold text-slate-700">Nessun documento in attesa</div>
                    <div className="mt-1 text-sm text-slate-500">Carica nuovi file con l'upload massivo qui sopra.</div>
                </div>

                {/* View mode toggle — only visible when there ARE pending items */}
                <div className={`mb-3 flex flex-wrap items-center justify-between gap-3 ${!loading && items.length > 0 ? "" : "hidden"}`}>
                    <div className="flex flex-wrap items-center gap-3">
                        <div className="text-sm text-slate-600">
                            <b className="font-mono-numbers text-[#376DB3]">{items.length}</b> documenti in attesa
                        </div>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={reassignAuto}
                            disabled={reassignBusy}
                            className="gap-2 border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                            data-testid="reassign-auto-btn"
                            title="Riprova l'assegnazione automatica usando il nome del file (cognome+nome, cognomenome, cognome singolo)"
                        >
                            {reassignBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                            {reassignBusy ? "Analizzo…" : "Riassegna automaticamente"}
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setAuditOpen(true)}
                            className="gap-2 border-violet-300 bg-violet-50 text-violet-700 hover:bg-violet-100"
                            data-testid="open-audit-btn"
                            title="Verifica i documenti già assegnati per individuare quelli assegnati al dipendente sbagliato"
                        >
                            <ShieldCheck className="h-4 w-4" />
                            Verifica assegnazioni
                        </Button>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <div className={`flex items-center gap-1.5 ${viewMode === "grouped" ? "" : "hidden"}`}>
                            <span className="text-xs text-slate-500">Raggruppa per:</span>
                            <div className="inline-flex rounded-md border border-slate-200 bg-white p-0.5">
                                <button
                                    type="button"
                                    onClick={() => setGroupDepth(1)}
                                    className={`rounded px-2 py-1 text-xs font-medium ${groupDepth === 1 ? "bg-[#376DB3] text-white" : "text-slate-600 hover:bg-slate-50"}`}
                                    title="Raggruppa solo per cognome"
                                    data-testid="depth-1-btn"
                                >
                                    Cognome
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setGroupDepth(2)}
                                    className={`rounded px-2 py-1 text-xs font-medium ${groupDepth === 2 ? "bg-[#376DB3] text-white" : "text-slate-600 hover:bg-slate-50"}`}
                                    title="Raggruppa per cognome + nome (consigliato)"
                                    data-testid="depth-2-btn"
                                >
                                    Cognome+Nome
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setGroupDepth(3)}
                                    className={`rounded px-2 py-1 text-xs font-medium ${groupDepth === 3 ? "bg-[#376DB3] text-white" : "text-slate-600 hover:bg-slate-50"}`}
                                    title="Raggruppa per cognome + nome + tipo documento"
                                    data-testid="depth-3-btn"
                                >
                                    + Categoria
                                </button>
                            </div>
                        </div>
                        <div className="inline-flex rounded-md border border-slate-200 bg-white p-0.5">
                            <button
                                type="button"
                                onClick={() => setViewMode("list")}
                                className={`rounded px-3 py-1 text-xs font-medium transition-colors ${viewMode === "list" ? "bg-[#376DB3] text-white" : "text-slate-600 hover:bg-slate-50"}`}
                                data-testid="view-list-btn"
                            >
                                Lista
                            </button>
                            <button
                                type="button"
                                onClick={() => setViewMode("grouped")}
                                className={`rounded px-3 py-1 text-xs font-medium transition-colors ${viewMode === "grouped" ? "bg-[#376DB3] text-white" : "text-slate-600 hover:bg-slate-50"}`}
                                data-testid="view-grouped-btn"
                            >
                                Raggruppato
                            </button>
                        </div>
                    </div>
                </div>

                {/* LIST view — original single-row layout */}
                <div
                    className={`space-y-3 ${!loading && items.length > 0 && viewMode === "list" ? "" : "hidden"}`}
                    data-testid="pending-list"
                >
                    {items.map((doc) => (
                        <PendingRow
                            key={doc.id}
                            doc={doc}
                            empOptions={empOptions}
                            busy={busyId === doc.id}
                            onAssign={confirmAssign}
                            onAssignMulti={assignDocToMulti}
                            onDelete={deletePending}
                            onPreview={setPreviewDoc}
                            onAssignToGroup={assignDocToGroup}
                            groups={groups}
                            empById={empById}
                        />
                    ))}
                </div>

                {/* GROUPED view — files with common prefix bundled together */}
                <div
                    className={`space-y-3 ${!loading && items.length > 0 && viewMode === "grouped" ? "" : "hidden"}`}
                    data-testid="pending-grouped"
                >
                    {groupedItems.map((group) => (
                        <GroupBlock
                            key={group.key}
                            group={group}
                            empOptions={empOptions}
                            empById={empById}
                            busyAny={groupAssignBusy || busyId !== null}
                            onBulkAssign={bulkAssignGroup}
                            onBulkDelete={bulkDeleteGroup}
                            onAssign={confirmAssign}
                            onAssignMulti={assignDocToMulti}
                            onDelete={deletePending}
                            onPreview={setPreviewDoc}
                            onAssignToGroup={assignDocToGroup}
                            groups={groups}
                        />
                    ))}
                </div>
            </main>
            <DocumentPreviewDialog
                open={!!previewDoc}
                document={previewDoc}
                onClose={() => setPreviewDoc(null)}
            />
            <AuditAssignmentsDialog open={auditOpen} onOpenChange={setAuditOpen} />
            <ErrorDetailsDialog
                open={showErrorsDialog}
                onClose={() => setShowErrorsDialog(false)}
                report={errorReport}
                onRetry={retryFailedFiles}
            />
        </div>
    );
}

// Block displaying a group of pending docs that share the same name prefix
// (cognome / cognome-nome / cognome-nome-categoria, depending on user-selected depth).
// Lets the user (a) bulk-assign all docs in the group to ONE employee, or
// (b) expand the group and assign each doc individually.
const GroupBlock = ({ group, empOptions, empById, busyAny, onBulkAssign, onBulkDelete, onAssign, onAssignMulti, onDelete, onPreview, onAssignToGroup, groups }) => {
    const [expanded, setExpanded] = useState(group.docs.length <= 3);
    const [bulkEmp, setBulkEmp] = useState("");
    const [bulkCat, setBulkCat] = useState("");
    const [bulkExp, setBulkExp] = useState("");
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const docs = group.docs;

    return (
        <div className="rounded-lg border border-[#376DB3]/30 bg-blue-50/30 shadow-sm" data-testid={`group-block-${group.key}`}>
            <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="flex w-full items-center justify-between px-4 py-3 text-left"
            >
                <div className="flex items-center gap-3 min-w-0">
                    <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md bg-[#376DB3]/10 text-[#376DB3]">
                        <span className="font-mono-numbers text-sm font-bold">{docs.length}</span>
                    </div>
                    <div className="min-w-0">
                        <div className="truncate font-mono text-sm font-medium text-slate-700">{group.prefix}</div>
                        <div className="text-xs text-slate-500">
                            {docs.length === 1 ? "1 file" : `${docs.length} file con stesso inizio nome`}
                        </div>
                    </div>
                </div>
                <span className="text-xs text-[#376DB3]">{expanded ? "Comprimi" : "Espandi"}</span>
            </button>

            {/* Bulk-assign toolbar — show only for groups with 2+ docs (no point bulk-assigning 1 file) */}
            <div className={`border-t border-[#376DB3]/20 bg-white p-3 ${docs.length >= 2 ? "" : "hidden"}`}>
                <div className="grid gap-2 sm:grid-cols-[1fr_180px_140px_auto_auto]">
                    <Select value={bulkEmp || "__none__"} onValueChange={(v) => setBulkEmp(v === "__none__" ? "" : v)}>
                        <SelectTrigger className="h-9" data-testid={`bulk-emp-${group.key}`}>
                            <SelectValue placeholder="Assegna tutti a…" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="__none__">— Scegli dipendente —</SelectItem>
                            {empOptions.map((e) => (
                                <SelectItem key={e.id} value={e.id}>{e.label}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Select value={bulkCat || "auto"} onValueChange={(v) => setBulkCat(v === "auto" ? "" : v)}>
                        <SelectTrigger className="h-9">
                            <SelectValue placeholder="Categoria" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="auto">Auto (da nome)</SelectItem>
                            {Object.entries(CATEGORY_LABELS).map(([v, l]) => (
                                <SelectItem key={v} value={v}>{l}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Input
                        type="date"
                        value={bulkExp}
                        onChange={(e) => setBulkExp(e.target.value)}
                        className="h-9"
                        placeholder="Scadenza"
                    />
                    <Button
                        size="sm"
                        onClick={() => onBulkAssign(docs, bulkEmp, bulkCat, bulkExp)}
                        disabled={busyAny || !bulkEmp}
                        className="bg-[#376DB3] text-white hover:bg-[#376DB3]/90"
                        data-testid={`bulk-assign-${group.key}`}
                    >
                        Assegna {docs.length}
                    </Button>
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setConfirmDeleteOpen(true)}
                        disabled={busyAny}
                        className="gap-1 border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                        data-testid={`bulk-delete-${group.key}`}
                        title={`Elimina tutti i ${docs.length} file di questo gruppo`}
                    >
                        <Trash2 className="h-4 w-4" />
                        Elimina {docs.length}
                    </Button>
                </div>
            </div>

            {/* Expanded individual rows */}
            <div className={`space-y-2 border-t border-slate-100 p-3 ${expanded ? "" : "hidden"}`}>
                {docs.map((doc) => (
                    <PendingRow
                        key={doc.id}
                        doc={doc}
                        empOptions={empOptions}
                        busy={false}
                        onAssign={onAssign}
                        onAssignMulti={onAssignMulti}
                        onDelete={onDelete}
                        onPreview={onPreview}
                        onAssignToGroup={onAssignToGroup}
                        groups={groups}
                        empById={empById}
                    />
                ))}
            </div>

            <AlertDialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
                <AlertDialogContent data-testid={`bulk-delete-confirm-${group.key}`}>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Eliminare {docs.length} file?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Tutti i file del gruppo <b className="font-mono">{group.prefix}</b> verranno eliminati definitivamente
                            dalla coda di attesa. Questa operazione non può essere annullata dalla UI.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid={`bulk-delete-cancel-${group.key}`}>Annulla</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => { setConfirmDeleteOpen(false); onBulkDelete(docs); }}
                            className="bg-red-600 hover:bg-red-700"
                            data-testid={`bulk-delete-confirm-btn-${group.key}`}
                        >
                            Elimina {docs.length} file
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};


const PendingRow = ({ doc, empOptions, busy, onAssign, onAssignMulti, onDelete, onPreview, onAssignToGroup, groups, empById }) => {
    const suggestedExists = doc.suggested_employee_id && empById.has(doc.suggested_employee_id);
    const [selected, setSelected] = useState(doc.suggested_employee_id || "");
    const [category, setCategory] = useState(doc.category || "altro");
    const [expires, setExpires] = useState(doc.expires_at || "");
    const [groupTarget, setGroupTarget] = useState("");
    const [showGroupPicker, setShowGroupPicker] = useState(false);
    const [multiOpen, setMultiOpen] = useState(false);

    return (
        <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm" data-testid={`pending-row-${doc.id}`}>
            {/* Header full width: filename on a single line */}
            <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md bg-amber-50 text-amber-600">
                    <AlertTriangle className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                    <button
                        type="button"
                        onClick={() => onPreview(doc)}
                        className="block w-full break-all text-left font-medium text-slate-800 hover:text-[#376DB3] hover:underline"
                        title={`Anteprima: ${doc.filename}`}
                        data-testid={`preview-filename-${doc.id}`}
                    >
                        {doc.filename}
                    </button>
                    <div className="mt-0.5 text-xs text-slate-500">
                        {formatBytes(doc.size_bytes)} · caricato {formatDateTime(doc.uploaded_at)}
                        <span className={doc.period ? "" : "hidden"}> · periodo <b className="text-slate-700">{doc.period}</b></span>
                    </div>
                </div>
            </div>
            <div className="mt-2 grid gap-4 xl:grid-cols-[minmax(0,1fr)_auto] xl:items-end">
                <div className="min-w-0 xl:pl-12">
                    <div
                        className={`inline-flex items-center gap-1.5 rounded-md bg-indigo-50 px-2 py-1 text-xs text-indigo-800 ${suggestedExists ? "" : "hidden"}`}
                        data-testid={`pending-suggestion-${doc.id}`}
                    >
                        <User className="h-3 w-3" />
                        Suggerito: <b>{doc.suggested_employee_name}</b>
                        <span className={`text-indigo-500 ${doc.suggested_match_score != null ? "" : "hidden"}`}>
                            ({Math.round((doc.suggested_match_score || 0) * 100)}%)
                        </span>
                    </div>
                    {/* Group assign row */}
                    <div className={`mt-2 flex flex-wrap items-center gap-2 ${showGroupPicker ? "" : "hidden"}`} data-testid={`group-picker-${doc.id}`}>
                        <Select value={groupTarget || "__none__"} onValueChange={(v) => setGroupTarget(v === "__none__" ? "" : v)}>
                            <SelectTrigger className="h-8 w-56 text-xs">
                                <SelectValue placeholder="Scegli gruppo…" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="__none__">— Scegli gruppo —</SelectItem>
                                {(groups || []).map((g) => (
                                    <SelectItem key={g.id} value={g.id}>{g.name} ({g.member_count})</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Button
                            size="sm"
                            onClick={() => onAssignToGroup(doc, groupTarget, category)}
                            disabled={!groupTarget || busy}
                            className="h-8 gap-1 bg-violet-600 px-3 text-xs text-white hover:bg-violet-700"
                            data-testid={`group-assign-confirm-${doc.id}`}
                        >
                            <Users className="h-3 w-3" /> Distribuisci
                        </Button>
                        <button
                            type="button"
                            onClick={() => setShowGroupPicker(false)}
                            className="text-xs text-slate-500 hover:text-slate-700"
                        >
                            Annulla
                        </button>
                    </div>
                    <button
                        type="button"
                        onClick={() => setShowGroupPicker(true)}
                        className={`mt-2 inline-flex items-center gap-1 text-xs font-medium text-violet-700 hover:text-violet-900 ${showGroupPicker ? "hidden" : ""}`}
                        data-testid={`group-assign-toggle-${doc.id}`}
                    >
                        <Users className="h-3 w-3" /> Assegna a un gruppo
                    </button>
                    <button
                        type="button"
                        onClick={() => setMultiOpen(true)}
                        className="ml-3 mt-2 inline-flex items-center gap-1 text-xs font-medium text-amber-700 hover:text-amber-900"
                        data-testid={`multi-assign-toggle-${doc.id}`}
                    >
                        <UsersRound className="h-3 w-3" /> Assegna a più dipendenti
                    </button>
                </div>
                <div className="grid items-end gap-2 sm:grid-cols-[1fr_1fr_1fr_auto_auto_auto] xl:grid-cols-[14rem_11rem_10rem_auto_auto_auto]">
                    <div className="min-w-0">
                        <Label className="text-xs">Assegna a</Label>
                        <Select value={selected} onValueChange={setSelected}>
                            <SelectTrigger className="mt-1" data-testid={`assign-select-${doc.id}`}>
                                <SelectValue placeholder="Seleziona dipendente" />
                            </SelectTrigger>
                            <SelectContent className="max-h-80">
                                {empOptions.map((e) => (
                                    <SelectItem key={e.id} value={e.id}>
                                        {e.label}{e.cessato ? " · CESSATO" : ""}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="min-w-0">
                        <Label className="text-xs">Categoria</Label>
                        <Select value={category} onValueChange={setCategory}>
                            <SelectTrigger className="mt-1" data-testid={`assign-cat-${doc.id}`}>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {CATEGORY_ORDER.map((c) => (
                                    <SelectItem key={c} value={c}>{CATEGORY_LABELS[c]}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="min-w-0">
                        <Label className="text-xs">Scadenza</Label>
                        <div className="mt-1">
                            <DatePicker
                                value={expires}
                                onChange={setExpires}
                                placeholder="No scadenza"
                                fromYear={new Date().getFullYear()}
                                toYear={new Date().getFullYear() + 5}
                                data-testid={`assign-exp-${doc.id}`}
                            />
                        </div>
                    </div>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onPreview(doc)}
                        className="h-10 gap-1 border-slate-300"
                        title="Anteprima documento"
                        data-testid={`preview-btn-${doc.id}`}
                    >
                        <Eye className="h-4 w-4" />
                    </Button>
                    <Button
                        onClick={() => onAssign(doc, selected, category, expires)}
                        disabled={!selected || busy}
                        className="h-10 gap-2 bg-emerald-600 text-white hover:bg-emerald-700"
                        data-testid={`assign-btn-${doc.id}`}
                    >
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserCheck className="h-4 w-4" />}
                        Conferma
                    </Button>
                    <Button
                        variant="outline"
                        onClick={() => onDelete(doc)}
                        disabled={busy}
                        className="h-10 gap-2 border-red-200 text-red-600 hover:bg-red-50"
                        data-testid={`delete-btn-${doc.id}`}
                    >
                        <Trash2 className="h-4 w-4" />
                    </Button>
                </div>
            </div>
            <MultiAssignDialog
                open={multiOpen}
                onClose={() => setMultiOpen(false)}
                doc={doc}
                empOptions={empOptions}
                category={category}
                expires={expires}
                onAssignMulti={onAssignMulti}
            />
        </div>
    );
};


// Multi-employee assignment: one pending file → many employees at once.
// Search + checkbox list + visible selection count.
const MultiAssignDialog = ({ open, onClose, doc, empOptions, category, expires, onAssignMulti }) => {
    const [search, setSearch] = useState("");
    const [selectedIds, setSelectedIds] = useState([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) {
            setSearch("");
            setSelectedIds([]);
            setSaving(false);
        }
    }, [open]);

    const filtered = (() => {
        const q = search.trim().toLowerCase();
        if (!q) return empOptions.slice(0, 100);
        return empOptions.filter((e) =>
            (e.label || "").toLowerCase().includes(q)
        ).slice(0, 100);
    })();

    const toggle = (id) => {
        setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
    };

    const selectAllFiltered = () => {
        const ids = filtered.map((e) => e.id);
        const merged = Array.from(new Set([...selectedIds, ...ids]));
        setSelectedIds(merged);
    };

    const clearAll = () => setSelectedIds([]);

    const handleConfirm = async () => {
        if (!selectedIds.length) return;
        setSaving(true);
        try {
            await onAssignMulti(doc, selectedIds, category, expires);
            onClose();
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-xl" data-testid={`multi-assign-dialog-${doc.id}`}>
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 font-display">
                        <UsersRound className="h-5 w-5 text-amber-600" />
                        Assegna a più dipendenti
                    </DialogTitle>
                    <DialogDescription className="truncate font-mono text-xs">
                        {doc?.filename}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-3">
                    <div className="relative">
                        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                        <Input
                            placeholder="Cerca per cognome, nome o matricola…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="pl-9"
                            data-testid={`multi-assign-search-${doc.id}`}
                            autoFocus
                        />
                    </div>

                    <div className="flex items-center justify-between text-xs text-slate-600">
                        <span>
                            <b data-testid={`multi-assign-count-${doc.id}`}>{selectedIds.length}</b> selezionat{selectedIds.length === 1 ? "o" : "i"}
                            <span className="ml-2 text-slate-400">· mostrati {filtered.length} di {empOptions.length}</span>
                        </span>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={selectAllFiltered}
                                disabled={!filtered.length}
                                className="font-medium text-[#376DB3] hover:underline disabled:opacity-50"
                                data-testid={`multi-assign-select-all-${doc.id}`}
                            >
                                Seleziona tutti
                            </button>
                            <span className="text-slate-300">·</span>
                            <button
                                type="button"
                                onClick={clearAll}
                                disabled={!selectedIds.length}
                                className="font-medium text-slate-600 hover:underline disabled:opacity-50"
                                data-testid={`multi-assign-clear-${doc.id}`}
                            >
                                Pulisci
                            </button>
                        </div>
                    </div>

                    <div className="max-h-80 overflow-y-auto rounded-md border border-slate-200" data-testid={`multi-assign-list-${doc.id}`}>
                        {filtered.length === 0 ? (
                            <div className="px-3 py-6 text-center text-sm text-slate-500">Nessun dipendente trovato</div>
                        ) : (
                            filtered.map((e) => {
                                const checked = selectedIds.includes(e.id);
                                return (
                                    <label
                                        key={e.id}
                                        className={`flex w-full cursor-pointer items-center gap-3 px-3 py-2 text-sm hover:bg-slate-50 ${checked ? "bg-amber-50/60" : ""}`}
                                        data-testid={`multi-assign-option-${e.id}`}
                                    >
                                        <Checkbox
                                            checked={checked}
                                            onCheckedChange={() => toggle(e.id)}
                                            data-testid={`multi-assign-checkbox-${e.id}`}
                                        />
                                        <span className="flex-1 truncate">{e.label}{e.cessato ? " · CESSATO" : ""}</span>
                                        {checked && <CheckCircle2 className="h-4 w-4 text-amber-600" />}
                                    </label>
                                );
                            })
                        )}
                    </div>

                    <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                        Categoria: <b>{CATEGORY_LABELS[category] || category}</b>
                        {expires && <span className="ml-2">· Scadenza: <b>{expires}</b></span>}
                        <span className="ml-2 text-slate-400">(modifica nei campi della riga se necessario)</span>
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose} data-testid={`multi-assign-cancel-${doc.id}`}>Annulla</Button>
                    <Button
                        onClick={handleConfirm}
                        disabled={!selectedIds.length || saving}
                        className="gap-2 bg-amber-600 text-white hover:bg-amber-700"
                        data-testid={`multi-assign-confirm-${doc.id}`}
                    >
                        {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                        Assegna a {selectedIds.length} dipendent{selectedIds.length === 1 ? "e" : "i"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};


// Classifies free-text error messages from the backend into human-friendly buckets
const classifyError = (msg) => {
    const m = (msg || "").toLowerCase();
    if (m.includes("file vuoto") || m.includes("vuoto") || m.includes("empty")) return "vuoto";
    if (m.includes("50 mb") || m.includes("50mb") || m.includes("too large") || m.includes("troppo grande")) return "troppo_grande";
    if (m.includes("timeout") || m.includes("network") || m.includes("econn") || m.includes("rete") || m.includes("lotto")) return "rete";
    if (m.includes("storage") || m.includes("s3") || m.includes("upload")) return "storage";
    return "altro";
};

const BUCKET_META = {
    vuoto: { label: "File vuoti (0 byte)", color: "bg-amber-50 text-amber-800 border-amber-200", hint: "L'export legacy potrebbe aver prodotto file corrotti — verifica se esistono nel sistema originale." },
    troppo_grande: { label: "File troppo grandi (> 50 MB)", color: "bg-orange-50 text-orange-800 border-orange-200", hint: "I PDF di buste paga normalmente sono <500 KB. Verifica scansioni a risoluzione troppo alta." },
    rete: { label: "Problemi di rete o timeout", color: "bg-red-50 text-red-800 border-red-200", hint: "Riprova solo questi file — la connessione potrebbe essere stata instabile." },
    storage: { label: "Errori di archiviazione", color: "bg-red-50 text-red-800 border-red-200", hint: "Storage temporaneamente non disponibile. Riprova tra qualche minuto." },
    altro: { label: "Altri errori", color: "bg-slate-50 text-slate-700 border-slate-200", hint: "Errori non classificati — controlla il singolo messaggio." },
};

const downloadErrorsCsv = (errors) => {
    const header = "filename,error\n";
    const rows = errors.map((e) => `"${(e.filename || "").replace(/"/g, '""')}","${(e.error || "").replace(/"/g, '""')}"`).join("\n");
    const blob = new Blob([header + rows], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `errori_upload_${new Date().toISOString().slice(0, 19).replace(/:/g, "-")}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};

const ErrorDetailsDialog = ({ open, onClose, report, onRetry }) => {
    const errors = report?.errors || [];
    const buckets = errors.reduce((acc, e) => {
        const b = classifyError(e.error);
        (acc[b] = acc[b] || []).push(e);
        return acc;
    }, {});
    const orderedBuckets = ["vuoto", "troppo_grande", "rete", "storage", "altro"].filter((k) => buckets[k]?.length);

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-3xl" data-testid="error-details-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 font-display">
                        <FileWarning className="h-5 w-5 text-amber-600" />
                        Dettagli errori upload — {errors.length} file
                    </DialogTitle>
                    <DialogDescription>
                        Gli errori sono raggruppati per causa. I file caricati con successo sono già nel sistema, qui vedi solo quelli falliti.
                    </DialogDescription>
                </DialogHeader>

                <div className="max-h-[60vh] space-y-3 overflow-y-auto">
                    {orderedBuckets.map((k) => {
                        const meta = BUCKET_META[k];
                        const list = buckets[k];
                        return (
                            <div key={k} className={`rounded-md border p-3 ${meta.color}`}>
                                <div className="flex items-center justify-between gap-2">
                                    <div className="font-display text-sm font-semibold">
                                        {meta.label}
                                        <span className="ml-2 rounded-full bg-white/70 px-2 py-0.5 text-xs font-mono-numbers">{list.length}</span>
                                    </div>
                                </div>
                                <div className="mt-1 text-xs opacity-80">{meta.hint}</div>
                                <details className="mt-2">
                                    <summary className="cursor-pointer text-xs font-medium underline">Mostra elenco file ({list.length})</summary>
                                    <ul className="mt-2 max-h-48 space-y-0.5 overflow-y-auto rounded bg-white/60 p-2 text-xs">
                                        {list.slice(0, 200).map((e, i) => (
                                            <li key={`${e.filename}-${i}`} className="truncate font-mono" title={e.filename}>{e.filename}</li>
                                        ))}
                                        {list.length > 200 && (
                                            <li className="text-xs italic opacity-70">… e altri {list.length - 200} file (scarica il CSV per la lista completa)</li>
                                        )}
                                    </ul>
                                </details>
                            </div>
                        );
                    })}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => downloadErrorsCsv(errors)} className="gap-2" data-testid="export-errors-csv-btn">
                        <Download className="h-4 w-4" />
                        Esporta CSV ({errors.length})
                    </Button>
                    <Button
                        variant="outline"
                        onClick={onRetry}
                        className="gap-2 border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                        data-testid="retry-failed-dialog-btn"
                    >
                        <RotateCcw className="h-4 w-4" />
                        Riprova solo questi {errors.length} file
                    </Button>
                    <Button onClick={onClose} className="bg-[#376DB3] text-white hover:bg-[#376DB3]/90">Chiudi</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
